import { useMutation } from "@tanstack/react-query";
import { Check, Loader2, Pencil, Plus, X } from "lucide-react";
import { useState } from "react";
import type { WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RejectionNotice } from "@/modules/orders/screens/RejectionNotice";
import { useOrderWrite } from "@/modules/orders/useOrderWrite";

/**
 * An open edit: what is being typed, and the `channelUpdatedAt` observed
 * when it was opened.
 *
 * The guard is captured once at that click and never re-read from the order
 * afterwards — `channelUpdatedAt` is a query result that a background
 * refetch can replace while the input is open, and sending that newer value
 * at submit would defeat the conflict check (#166): the write would sail over
 * a change made in Shopify admin in the meantime instead of being refused.
 */
interface Draft {
  draft: string;
  guard: string;
}

/**
 * The order's tags, added and removed through Shopify (#168).
 *
 * `tagsAdd` and `tagsRemove` are deltas, so a tag someone adds in Shopify
 * admin while this is open is not dropped by it. A refusal leaves the draft
 * where it was, with Shopify's own words underneath.
 */
export function OrderTagsEditor({
  orderId,
  channelUpdatedAt,
  tags,
  canEdit,
}: {
  orderId: string;
  channelUpdatedAt: string;
  tags: string[];
  canEdit: boolean;
}) {
  const data = useDataSource();
  const write = useOrderWrite(orderId);
  const [adding, setAdding] = useState<Draft | null>(null);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const mutation = useMutation({
    mutationFn: (input: {
      tag: string;
      guard: string;
      mode: "add" | "remove";
    }) =>
      input.mode === "add"
        ? data.orders.addTag(orderId, input.tag, {
            expectedUpdatedAt: input.guard,
          })
        : data.orders.removeTag(orderId, input.tag, {
            expectedUpdatedAt: input.guard,
          }),
    onSuccess: (result) => {
      if (result.ok) {
        setAdding(null);
        setRejection(null);
        write.accepted(result.data);
      } else {
        setRejection(result.rejection);
      }
    },
  });

  function submit() {
    if (!adding || adding.draft.trim() === "") return;
    setRejection(null);
    mutation.mutate({ tag: adding.draft, guard: adding.guard, mode: "add" });
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.map((tag) => (
          <Badge
            key={tag}
            variant="secondary"
            className={canEdit ? "gap-1 py-1 pr-1 font-normal" : "font-normal"}
          >
            {tag}
            {canEdit ? (
              <button
                type="button"
                aria-label={`Remove tag ${tag}`}
                disabled={mutation.isPending}
                onClick={() => {
                  setRejection(null);
                  mutation.mutate({
                    tag,
                    guard: channelUpdatedAt,
                    mode: "remove",
                  });
                }}
                className="hover:bg-secondary-foreground/10 rounded-full p-0.5 disabled:pointer-events-none disabled:opacity-50"
              >
                <X className="size-3" />
              </button>
            ) : null}
          </Badge>
        ))}

        {tags.length === 0 && adding === null ? (
          <p className="text-muted-foreground">No tags</p>
        ) : null}

        {canEdit && adding === null ? (
          <Button
            type="button"
            size="xs"
            variant="ghost"
            aria-label="Add a tag"
            onClick={() => {
              setRejection(null);
              setAdding({ draft: "", guard: channelUpdatedAt });
            }}
          >
            <Plus />
            Add
          </Button>
        ) : null}
      </div>

      {adding !== null ? (
        <div className="flex items-center gap-1">
          <Input
            autoFocus
            value={adding.draft}
            placeholder="New tag"
            aria-label="New tag"
            className="h-8 min-w-0 flex-1"
            disabled={mutation.isPending}
            onChange={(event) => {
              setAdding({ ...adding, draft: event.target.value });
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") submit();
              if (event.key === "Escape") {
                setAdding(null);
                setRejection(null);
              }
            }}
          />
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Save tag"
            disabled={adding.draft.trim() === "" || mutation.isPending}
            onClick={submit}
          >
            {mutation.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Check />
            )}
          </Button>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Cancel"
            disabled={mutation.isPending}
            onClick={() => {
              setAdding(null);
              setRejection(null);
            }}
          >
            <X />
          </Button>
        </div>
      ) : null}

      {rejection ? (
        <RejectionNotice
          rejection={rejection}
          onReload={() => {
            void write.reload();
            setAdding(null);
            setRejection(null);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The order note, set through Shopify with `orderUpdate` (#168). A refusal
 * keeps the textarea open with what was typed.
 */
export function OrderNoteEditor({
  orderId,
  channelUpdatedAt,
  note,
  canEdit,
}: {
  orderId: string;
  channelUpdatedAt: string;
  note: string | null;
  canEdit: boolean;
}) {
  const data = useDataSource();
  const write = useOrderWrite(orderId);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const mutation = useMutation({
    mutationFn: (input: Draft) =>
      data.orders.setNote(orderId, input.draft, {
        expectedUpdatedAt: input.guard,
      }),
    onSuccess: (result) => {
      if (result.ok) {
        setEditing(null);
        setRejection(null);
        write.accepted(result.data);
      } else {
        setRejection(result.rejection);
      }
    },
  });

  if (editing === null) {
    return (
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {note?.trim() ? (
            <p className="whitespace-pre-line break-words">{note}</p>
          ) : (
            <p className="text-muted-foreground">No notes from customer</p>
          )}
        </div>
        {canEdit ? (
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            aria-label="Edit note"
            onClick={() => {
              setRejection(null);
              setEditing({ draft: note ?? "", guard: channelUpdatedAt });
            }}
          >
            <Pencil />
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        autoFocus
        rows={3}
        aria-label="Order note"
        value={editing.draft}
        disabled={mutation.isPending}
        onChange={(event) => {
          setEditing({ ...editing, draft: event.target.value });
        }}
      />
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={mutation.isPending}
          onClick={() => {
            setRejection(null);
            mutation.mutate(editing);
          }}
        >
          {mutation.isPending ? <Loader2 className="animate-spin" /> : null}
          Save
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={mutation.isPending}
          onClick={() => {
            setEditing(null);
            setRejection(null);
          }}
        >
          Cancel
        </Button>
      </div>
      {rejection ? (
        <RejectionNotice
          rejection={rejection}
          onReload={() => {
            void write.reload();
            setEditing(null);
            setRejection(null);
          }}
        />
      ) : null}
    </div>
  );
}
